/**
 * apiErrorHandler.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Single source of truth for extracting human-readable error messages from
 * any API response, thrown string, or Error object across the entire LM frontend.
 *
 * The backend responds with this shape on errors:
 *   { success: false, statusCode: number, message: string, error: { code, details? } }
 *
 * Usage:
 *   import { extractErrorMessage } from "../utilities/apiErrorHandler";
 *
 *   onError: (error) => toast.error(extractErrorMessage(error))
 */

/** Shape of our standardised backend error body */
interface ApiErrorBody {
  success: false;
  statusCode?: number;
  message?: string;
  error?: {
    code?: string;
    details?: Array<{ field: string; message: string }>;
  };
  details?: Array<{ field: string; message: string }>;
}

/**
 * Extracts the best user-facing message from any error value.
 * Priority:
 *   1. Validation details from Zod (field-level messages joined together)
 *   2. `message` field from the backend JSON body
 *   3. The error string itself (legacy: thrown strings from mutations)
 *   4. `error.message` (standard Error object)
 *   5. Fallback: "Something went wrong"
 */
export function extractErrorMessage(error: unknown, fallback = "Something went wrong"): string {
  if (!error) return fallback;

  // Case 1: string thrown directly (legacy pattern: throw res.message || "...")
  if (typeof error === "string" && error.length > 0) return error;

  if (typeof error === "object") {
    const e = error as any;

    // Case 2: ApiError object with { body: ApiErrorBody } — from our new fetch wrappers
    if (e?.body) {
      const body = e.body as ApiErrorBody;

      // Zod validation details: join all field messages
      const details = body?.details || body?.error?.details;
      if (details?.length) {
        return details.map((d) => d.message).join(" · ");
      }

      if (body?.message) return body.message;
    }

    // Case 3: Standard Error — message is already a string from backend
    if (e?.message && typeof e.message === "string") return e.message;

    // Case 3.5: Axios Error shape
    const axiosMessage = e?.response?.data?.message || e?.response?.data?.error?.message;
    if (axiosMessage && typeof axiosMessage === "string") return axiosMessage;

    // Case 4: Raw backend body shape without wrapper
    const details = e?.details || e?.error?.details;
    if (details?.length) {
      return details.map((d: any) => d.message).join(" · ");
    }
  }

  return fallback;
}

/**
 * Creates an Error whose `.message` is the backend message string.
 * Use inside mutationFn so React Query's onError receives a proper Error.
 *
 * @example
 *   if (!res.success) throw createApiError(res);
 */
export function createApiError(res: ApiErrorBody): Error {
  let message = res?.message || "Something went wrong";

  const details = res?.details || (res as any)?.errors || res?.error?.details; 

  if (Array.isArray(details) && details.length > 0) {
    const firstErrorMessage = details[0]?.message;

    if (firstErrorMessage) {
      message = firstErrorMessage;
    }
  }

  const err = new Error(message) as Error & {
    body: ApiErrorBody;
    statusCode?: number;
  };

  err.body = res;
  err.statusCode = res?.statusCode;
  return err;
}
